import React, { useState, useEffect, useCallback } from 'react';
import { Eye, EyeOff, Copy, Check, ExternalLink, Clock } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Skeleton from '../ui/Skeleton';
import { useCredential } from '../../hooks/useCredentials';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';

const TIMEOUT_SECONDS = 30;

const CopyButton = ({ value }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // clipboard not available
        }
    };

    return (
        <button
            onClick={handleCopy}
            className="p-1 rounded text-text-muted hover:text-accent transition-colors"
            title="Copy to clipboard"
        >
            {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
    );
};

const ViewCredentialModal = ({ isOpen, onClose, credentialUid }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(TIMEOUT_SECONDS);
    const { t } = useLanguage();

    const { data: credential, isLoading } = useCredential(credentialUid, {
        enabled: isOpen && !!credentialUid,
    });

    // Reset state and start countdown when modal opens
    useEffect(() => {
        if (!isOpen) {
            setShowPassword(false);
            setSecondsLeft(TIMEOUT_SECONDS);
            return;
        }

        setSecondsLeft(TIMEOUT_SECONDS);

        const interval = setInterval(() => {
            setSecondsLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    onClose();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const progress = (secondsLeft / TIMEOUT_SECONDS) * 100;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={credential?.title || t(KEYS.CREDENTIAL_VIEW_TITLE)}
            subtitle={t(KEYS.COMMON_CLOSE)}
            size="medium"
            showCloseButton
        >
            <div className="p-4 space-y-4">
                {isLoading ? (
                    <div className="space-y-3">
                        <Skeleton variant="text" count={4} />
                    </div>
                ) : credential ? (
                    <>
                        {/* Link */}
                        {credential.link && (
                            <div className="space-y-1">
                                <span className="label-base">Portal Link</span>
                                <div className="flex items-center gap-2">
                                    <a
                                        href={credential.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-accent hover:underline flex items-center gap-1 truncate"
                                    >
                                        {credential.link}
                                        <ExternalLink className="w-3 h-3 shrink-0" />
                                    </a>
                                    <CopyButton value={credential.link} />
                                </div>
                            </div>
                        )}

                        {/* Username */}
                        {credential.username && (
                            <div className="space-y-1">
                                <span className="label-base">{t(KEYS.CREDENTIAL_VIEW_USERNAME)}</span>
                                <div className="flex items-center gap-2 px-3 py-2 bg-surface-overlay rounded border border-surface-border">
                                    <span className="text-sm text-text-primary font-mono flex-1 select-all">
                                        {credential.username}
                                    </span>
                                    <CopyButton value={credential.username} />
                                </div>
                            </div>
                        )}

                        {/* Password */}
                        {credential.password && (
                            <div className="space-y-1">
                                <span className="label-base">{t(KEYS.CREDENTIAL_VIEW_PASSWORD)}</span>
                                <div className="flex items-center gap-2 px-3 py-2 bg-surface-overlay rounded border border-surface-border">
                                    <span className="text-sm text-text-primary font-mono flex-1 select-all tracking-widest">
                                        {showPassword ? credential.password : '•'.repeat(Math.min(credential.password.length, 16))}
                                    </span>
                                    <button
                                        onClick={() => setShowPassword((v) => !v)}
                                        className="p-1 rounded text-text-muted hover:text-accent transition-colors"
                                        title={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword
                                            ? <EyeOff className="w-3.5 h-3.5" />
                                            : <Eye className="w-3.5 h-3.5" />
                                        }
                                    </button>
                                    <CopyButton value={credential.password} />
                                </div>
                            </div>
                        )}
                    </>
                ) : null}

                {/* Countdown */}
                <div className="pt-2 space-y-1.5">
                    <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-xs text-text-muted">
                            <Clock className="w-3 h-3" />
                            Closing in {secondsLeft}s
                        </span>
                        <Button variant="ghost" size="small" onClick={onClose} className="text-xs text-text-muted">
                            {t(KEYS.COMMON_CLOSE)}
                        </Button>
                    </div>
                    <div className="w-full h-1 bg-surface-border rounded-full overflow-hidden">
                        <div
                            className="h-full bg-accent/60 rounded-full transition-all duration-1000 ease-linear"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ViewCredentialModal;
